import { useCallback, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { assignOwnerToRfidCard, createNewRfidCard, deactivateRfidCard, deleteRfidCard, getAllRfidCards, unassignOwnerFromRfidCard, updateRfidCard } from "../../../services/rfidCards";

export interface RfidCard {
	uid: string;
	card_code: string;
	status: "INACTIVE" | "ACTIVE" | "DEACTIVATED"
	emaid: string;
	created_at: string;
	updated_at: string;
	valid_until: string | null;
}

type ToastLevel = "success" | "error" | "warn" | "info";

const showToast = (
	toastRef: React.RefObject<Toast>,
	severity: ToastLevel,
	summary: string,
	detail: string,
	life = 4000
) => {
	toastRef.current?.show({ severity, summary, detail, life });
};

export function useRfidCards(toastRef: React.RefObject<Toast>) {
	const [rfidCards, setRfidCards] = useState<RfidCard[]>([]);
	const [initialLoading, setInitialLoading] = useState<boolean>(true);
	const initialLoadDone = useRef(false);
	const [refreshing, setRefreshing] = useState<boolean>(false);

	// First load is tracked in a ref, not read off initialLoading: depending on
	// the state would rebuild this callback as soon as the first load finished,
	// and the mount effect below would fire a second request.
	const fetchRfidCards = useCallback(async () => {
		const firstLoad = !initialLoadDone.current;
		if (!firstLoad) setRefreshing(true);
		try {
			const data = await getAllRfidCards();
			setRfidCards(data);
		} catch (e: any) {
			const msg = e?.response?.data?.error ?? "Failed to load RFID cards.";
			showToast(toastRef, "error", "Error", msg);
		} finally {
			if (firstLoad) {
				initialLoadDone.current = true;
				setInitialLoading(false);
			} else setRefreshing(false);
		}
	}, [toastRef]);

	const handleRequest = useCallback(async (
		action: () => Promise<void>,
		successMessage: string,
		errorMessage: string
	) => {
		try {
			await action();
			showToast(toastRef, "success", "Success", successMessage);
			await fetchRfidCards();
		} catch (e: any) {
			const msg = e?.response?.data?.error ?? errorMessage;
			showToast(toastRef, "error", "Error", msg);
			throw e;
		}
	}, [fetchRfidCards, toastRef]);

	const createRfidCard = useCallback(
		async (uid: string, cardCode: string, validUntil: string | null = null) => {
			if (!uid || !cardCode) {
				showToast(toastRef, "warn", "Warning", "Rfid Uid and card code cannot be empty");
				return;
			}
			await handleRequest(
				() => createNewRfidCard(uid, cardCode, validUntil),
				"RFID card added successfully.",
				"Failed to add RFID card."
			);
		},
		[handleRequest, toastRef]
	);

	const assignOwnerToCard = useCallback(
		async (uid: string, emaid: string) => {
			await handleRequest(
				() => assignOwnerToRfidCard(uid, emaid),
				"RFID card assigned successfully.",
				"Failed to assign owner to RFID card."
			);
		},
		[handleRequest]
	);

	const unassignOwnerFromCard = useCallback(
		async (uid: string) => {
			await handleRequest(
				() => unassignOwnerFromRfidCard(uid),
				"RFID card unassigned successfully.",
				"Failed to unassign owner from RFID card."
			);
		},
		[handleRequest]
	);

	const deactivateCard = useCallback(
		async (uid: string) => {
			await handleRequest(
				() => deactivateRfidCard(uid),
				"RFID card deactivated successfully.",
				"Failed to deactivate RFID card."
			);
		},
		[handleRequest]
	);

	const deleteCard = useCallback(
		async (uid: string) => {
			await handleRequest(
				() => deleteRfidCard(uid),
				"RFID card deleted successfully.",
				"Failed to delete RFID card."
			);
		},
		[handleRequest]
	);

	const updateCard = useCallback(
		async (uid: string, cardCode: string, validUntil: string | null = null) => {
			await handleRequest(
				() => updateRfidCard(uid, cardCode, validUntil),
				"RFID card updated successfully.",
				"Failed to update RFID card."
			);
		},
		[handleRequest]
	);

	useEffect(() => {
		fetchRfidCards();
	}, [fetchRfidCards]);

	return { rfidCards, initialLoading, refreshing, fetchRfidCards, assignOwnerToCard, createRfidCard, unassignOwnerFromCard, deactivateCard, deleteCard, updateCard };
}